import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Ruler, Mail, BadgeCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquireDialog from "@/components/EnquireDialog";
import FitFinderDialog from "@/components/FitFinderDialog";
import WaitlistDialog from "@/components/WaitlistDialog";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { formatMoney } from "@/lib/currency";
import { formatWeight, lengthLabel, cmToInput } from "@/lib/units";

const SUBSCRIBED_KEY = "mp_waitlist_subscriptions_v1";

export default function ProductDetail() {
  const { id } = useParams();
  const { settings } = useSettings();
  const [item, setItem] = useState(null);
  const [active, setActive] = useState(0);
  const [enquireOpen, setEnquireOpen] = useState(false);
  const [fitOpen, setFitOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [alreadyOnList, setAlreadyOnList] = useState(false);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(SUBSCRIBED_KEY) || "{}");
      setAlreadyOnList(Boolean(stored[id]));
    } catch (e) { /* noop */ }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/storefront/inventory/${id}`)
      .then((r) => { if (!cancelled) setItem(r.data); })
      .catch((e) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("product fetch failed", e);
        }
      });
    return () => { cancelled = true; };
  }, [id]);

  // Memoize the spec rows so we don't recompute map/filter on every render.
  const specRows = useMemo(() => {
    if (!item) return [];
    const us = settings?.unit_system || "metric";
    const base = [
      ["fabric", item.fabric],
      ["colour", item.color],
      [item.category_name ? "category" : null, item.category_name],
      ["size", item.size],
      ["type", item.type],
      ["style", item.style],
    ];
    // Typed measurements override the free-text fallback when a schema is present.
    const schema = item.category_schema || [];
    const map = item.measurements_map || {};
    const measurementRows = [];
    if (schema.length) {
      for (const f of schema) {
        const v = map[f.key];
        if (v === undefined || v === null || v === "") continue;
        if (f.unit_kind === "length") {
          measurementRows.push([f.label.toLowerCase(), `${cmToInput(v, us)} ${lengthLabel(us)}`]);
        } else {
          measurementRows.push([f.label.toLowerCase(), String(v)]);
        }
      }
    } else if (item.measurements) {
      measurementRows.push([`measurements (${lengthLabel(us)})`, item.measurements]);
    }
    return [
      ...base,
      ...measurementRows,
      ["weight", item.weight ? formatWeight(item.weight, us) : ""],
    ].filter(([k, v]) => k && v);
  }, [item, settings]);

  // Only show the "will this fit me?" button when the piece has at least one
  // typed length measurement we can compare against — otherwise it's noise.
  const hasFitMeasurements = useMemo(() => {
    if (!item) return false;
    const schema = item.category_schema || [];
    const map = item.measurements_map || {};
    return schema.some((f) => f.unit_kind === "length" && map[f.key] !== undefined && map[f.key] !== null && map[f.key] !== "");
  }, [item]);

  // Show "send me when my fit lands" when the piece isn't actively shoppable — either it's
  // already sold/reserved or still in a pre-listing pipeline stage. We don't want the CTA
  // to compete with the "enquire to acquire" button on a fresh, available piece.
  const waitlistEligible = useMemo(() => {
    if (!item) return false;
    const SHOPPABLE_STAGES = new Set(["Listing & Marketing", "Live on Shop"]);
    const reservedOrSold = ["Reserved", "Sold", "Shipped", "Delivered", "Returned"].includes(item.stage);
    const preListing = item.stage && !SHOPPABLE_STAGES.has(item.stage) && !reservedOrSold;
    return reservedOrSold || preListing;
  }, [item]);

  if (!item) {
    return (
      <div className="bg-ivory min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-8 lg:px-16 py-32 text-center">
          <p className="editorial-label">loading the piece…</p>
        </div>
        <Footer />
      </div>
    );
  }

  const mainPhoto = item.photos?.[active];

  return (
    <div className="bg-ivory min-h-screen" data-testid="product-detail-page">
      <Navbar />

      <div className="max-w-7xl mx-auto px-8 lg:px-16 py-12 grid lg:grid-cols-2 gap-16">
        {/* Images */}
        <div>
          <div className="aspect-[3/4] bg-linen overflow-hidden mb-4">
            {mainPhoto ? (
              <img src={fileUrl(mainPhoto)} alt={item.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-serif text-2xl lowercase text-taupe">awaiting studio</span>
              </div>
            )}
          </div>
          {item.photos?.length > 1 && (
            <div className="grid grid-cols-5 gap-3">
              {item.photos.map((p, i) => (
                <button key={p+i} onClick={() => setActive(i)} className={`aspect-square bg-linen overflow-hidden ${i === active ? "outline outline-1 outline-brass" : ""}`}>
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="lg:py-8">
          <Link to="/shop" className="editorial-label hover:text-brass">← back to the edit</Link>
          <p className="editorial-label mt-10">{item.brand || "—"}</p>
          <h1 className="font-serif text-5xl lowercase text-charcoal mt-2">{item.title}</h1>
          <div className="mt-6 flex items-baseline gap-3 flex-wrap">
            {item.original_price > 0 && item.original_price > item.sale_price ? (
              <>
                <p className="text-2xl font-serif text-brass">{formatMoney(item.sale_price)}</p>
                <p className="text-lg font-serif text-taupe line-through">{formatMoney(item.original_price)}</p>
                <span className="text-[0.65rem] uppercase tracking-widest text-brass border border-brass/40 px-2 py-0.5" data-testid="pdp-markdown-badge">
                  marked down · -{item.markdown_pct}%
                </span>
              </>
            ) : (
              <p className="text-2xl font-serif text-brass">{formatMoney(item.sale_price)}</p>
            )}
          </div>
          <span className="editorial-divider my-8 inline-block"></span>

          <p className="text-base text-espresso leading-relaxed mb-10 whitespace-pre-wrap">{item.description || "A piece chosen for its quiet character. Restored, ironed, and re-introduced to its next story."}</p>

          <dl className="grid grid-cols-2 gap-6 border-t border-taupe/40 pt-8">
            {specRows.map(([k, v]) => (
              <div key={k}>
                <dt className="editorial-label">{k}</dt>
                <dd className="text-sm text-charcoal mt-1 tracking-wide">{String(v).toLowerCase()}</dd>
              </div>
            ))}
          </dl>

          {hasFitMeasurements && (
            <button
              onClick={() => setFitOpen(true)}
              data-testid="open-fit-finder"
              className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase border border-brass text-brass hover:bg-brass hover:text-ivory px-5 py-2.5 transition-colors"
            >
              <Ruler className="w-3 h-3" /> will this fit me?
            </button>
          )}

          {/* Waitlist CTA — appears when the piece isn't actively shoppable so visitors
              can subscribe for the next matching piece instead of leaving empty-handed. */}
          {waitlistEligible && item?.category && (
            <button
              onClick={() => setWaitlistOpen(true)}
              data-testid="open-waitlist"
              className={`mt-3 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase border px-5 py-2.5 transition-colors ${alreadyOnList ? "border-taupe text-taupe cursor-default" : "border-brass text-brass hover:bg-brass hover:text-ivory"}`}
              disabled={alreadyOnList}
            >
              {alreadyOnList ? <><BadgeCheck className="w-3 h-3" /> you&apos;re on the list</> : <><Mail className="w-3 h-3" /> send me when my fit lands</>}
            </button>
          )}

          <button data-testid="enquire-btn" onClick={() => setEnquireOpen(true)} className="mt-12 bg-brass hover:bg-espresso text-ivory px-10 py-4 text-sm tracking-[0.25em] uppercase transition-colors duration-300">
            enquire to acquire
          </button>
          <p className="editorial-label mt-6">quiet luxury for everyday living.</p>
        </div>
      </div>

      <EnquireDialog open={enquireOpen} onClose={() => setEnquireOpen(false)} item={item} stripeEnabled={settings?.stripe_enabled} />
      <FitFinderDialog open={fitOpen} onClose={() => setFitOpen(false)} item={item} unitSystem={settings?.unit_system || "metric"} />
      <WaitlistDialog
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        item={item}
        unitSystem={settings?.unit_system || "metric"}
        onSubscribed={() => setAlreadyOnList(true)}
      />

      <Footer />
    </div>
  );
}
